import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { FaUser, FaEnvelope, FaBook, FaGraduationCap, FaCalendarAlt, FaCamera, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';

const Profile = () => {
  const { user, loading, enrolledCourses, fetchUserCourses, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [updating, setUpdating] = useState(false);
  const [avatarKey, setAvatarKey] = useState(Date.now());
  const fileInputRef = useRef(null);

  const BACKEND_URL = process.env.NODE_ENV === 'production'
    ? 'https://learnify-58mi.onrender.com'
    : 'http://localhost:5000';

  useEffect(() => {
    if (user) {
      fetchUserCourses();
    }
  }, [user, fetchUserCourses]);

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    const formData = new FormData();
    formData.append('avatar', file);

    setUpdating(true);
    try {
      const response = await api.post('/users/profile/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('Avatar upload response:', response.data);
      
      // Force refresh user data from server
      await refreshUser();
      
      // Force component re-render with new avatar
      setAvatarKey(Date.now());
      
      // Force a re-render by clearing the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      toast.success('Avatar updated successfully!');
    } catch (error) {
      console.error('Avatar upload error:', error);
      toast.error(error.response?.data?.message || 'Failed to update avatar.');
    } finally {
      setUpdating(false);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current.click();
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
          <FaSpinner className="fa-spin fa-3x text-primary" />
      </div>
    );
  }

  if (!user) {
      return (
          <div className="container py-5 text-center">
              <p>Please log in to view your profile.</p>
          </div>
      )
  }

  const avatarUrl = user.profileImage && user.profileImage !== '/default-profile.png' 
    ? `${BACKEND_URL}${user.profileImage}?t=${avatarKey}` 
    : `https://api.dicebear.com/8.x/micah/svg?seed=${user._id}`;

  return (
    <div className="container py-5" style={{ marginTop: '75px' }}>
      <div className="row">
        {/* Profile Information */}
        <div className="col-lg-4 mb-4">
          <div className="card shadow-sm">
            <div className="card-body text-center">
              <div className="position-relative d-inline-block mb-3">
                <img
                  src={avatarUrl}
                  alt="Profile Avatar"
                  className="rounded-circle"
                  style={{ 
                    width: '150px', 
                    height: '150px', 
                    objectFit: 'cover',
                    border: '4px solid #06BBCC',
                    cursor: 'pointer'
                  }}
                  onClick={handleAvatarClick}
                />
                <button
                  className="btn btn-primary rounded-circle position-absolute"
                  style={{
                    bottom: '10px',
                    right: '10px',
                    width: '40px',
                    height: '40px',
                    padding: '0',
                    border: '2px solid white'
                  }}
                  onClick={handleAvatarClick}
                  disabled={updating}
                >
                  {updating ? <FaSpinner className="fa-spin" /> : <FaCamera />}
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleAvatarChange}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
              </div>
              <h3 className="card-title mb-3">{user?.name || 'User'}</h3>
              <div className="text-muted mb-3">
                <FaEnvelope className="me-2" />
                {user?.email || 'No email provided'}
              </div>
              <div className="text-muted mb-3">
                <FaCalendarAlt className="me-2" />
                Joined {new Date(user?.createdAt).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>

        {/* Enrolled Courses */}
        <div className="col-lg-8">
          <div className="card shadow-sm">
            <div className="card-header bg-white">
              <h4 className="mb-0">
                <FaBook className="me-2 text-primary" />
                My Learning
              </h4>
            </div>
            <div className="card-body">
              {enrolledCourses.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted mb-3">You haven't enrolled in any courses yet.</p>
                  <a href="/courses" className="btn btn-primary">
                    Browse Courses
                  </a>
                </div>
              ) : (
                <div className="row">
                  {enrolledCourses.map((course) => (
                    <div key={course.id} className="col-md-6 mb-4">
                      <div className="card h-100 course-card-profile">
                        <img
                          src={course.thumbnail || 'https://via.placeholder.com/300x200'}
                          className="card-img-top"
                          alt={course.title}
                          style={{ height: '180px', objectFit: 'cover' }}
                        />
                        <div className="card-body d-flex flex-column">
                          <h5 className="card-title">{course.title}</h5>
                          <div className="progress mt-auto mb-2" style={{height: '8px'}}>
                            <div className="progress-bar" role="progressbar" style={{width: `${course.progress || 0}%`}} aria-valuenow={course.progress || 0} aria-valuemin="0" aria-valuemax="100"></div>
                          </div>
                          <button className="btn btn-outline-primary btn-sm" onClick={() => navigate(`/courses/${course.id}/content`)}>
                              Continue Learning
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;